'use client';

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import {
  MessageCircle,
  MessagesSquare,
  LayoutDashboard,
  PenSquare,
  Settings,
} from 'lucide-react';
import { useReactFlow, type NodeProps } from '@xyflow/react';
import { NodeFrame, type NodeFrameAction } from './node-frame';
import { Avatar } from '@/components/avatar';
import { useAuth } from '@/components/auth-provider';
import { ChatView } from '@/components/chat/chat-view';
import { AnonAgentChatPreview } from '@/components/chat/anon-agent-chat-preview';
import type { ChatHostHandlers } from '@/components/chat/chat-host-context';
import { useUAMP } from '@/hooks/useUAMP';
import { useDelegationVisualization } from '@/components/workspace/use-delegation-visualization';
import { emitDelegationEvent } from '@/lib/agents/delegation-events';
import { AgentSettingsPanel } from '@/components/agent-details';
import { Spinner } from '@/components/ui/spinner';
import {
  useWorkspace,
  useWorkspaceActions,
  useWorkspaceItem,
} from '@/components/workspace/store';
import { subscribeWorkspaceSpawn } from '@/lib/workspaces/spawn-events';
import { formatRelativeTime } from '@/lib/utils';
import { chatParticipants } from '@/lib/chat/participants';
import { useRegisterWidget } from '@/lib/workspaces/widget-command-bus-context';
import { BusError } from '@/lib/workspaces/widget-command-bus';
import { WIDGET_REGISTRY, resolveWidget, type WidgetSpec } from '@/lib/workspaces/widget-registry';
import { getModelTransport } from '@/lib/models/catalog';
import { getRuntimeConfig } from '@/lib/runtime-config';
import { WidgetBridge } from '@/components/workspace/widget-bridge';
import { getPlacement } from '@/lib/workspaces/types';
import { useWorkspaceTabId } from '@/lib/workspaces/use-workspace-tab-id';
import {
  resolveParticipant,
  getCachedParticipant,
} from '@/lib/workspaces/resolve-participant';
import { resolveDelegatedAgentFromSubChat } from '@/lib/chat/resolve-delegated-agent';
import { isHumanParticipant } from '@/lib/workspaces/participant-kind';

/**
 * Agent widget views.
 *
 * - `welcome`  : default state. Renders the embedded `ChatView` so the
 *   user always lands in the agent's own welcome experience — the
 *   draft-mode greeting + suggested prompts that the agent itself
 *   defines (no custom widget chrome on top). When `state.chatId` is
 *   set, the same `ChatView` shows the live chat instead of the
 *   draft greeting. In both cases NO state button is highlighted —
 *   "welcome" is the resting state of the widget.
 * - `chats`    : ephemeral picker. Shows a list of the user's chats
 *   with this agent. Click a row → save `chatId` and drop back to
 *   `welcome` (which now shows that chat). Click the Chats button
 *   again with no `chatId` → drop back to `welcome` (the agent
 *   greeting). The chats button is highlighted only while this list
 *   is on screen.
 * - `settings` : the embedded `AgentSettingsPanel`.
 *
 * When the agent declares a custom UI (`metadata.ui`), `welcome` renders that
 * widget by default; a header toggle (`state.chatMode`) lets the user switch
 * to the built-in chat experience (greeting + active chat) and back.
 *
 * `state.chatId` persists across reloads so a user who chatted with
 * the agent before sees that conversation when they re-open the
 * widget, instead of the agent's draft welcome.
 */
type View = 'welcome' | 'chats' | 'settings';

export type AgentNodeData = { itemId: string };

// Username → identity cache + de-duped fetcher live in
// `lib/workspaces/resolve-participant` so the delegation-viz hook and
// other workspace spawn paths share the same cache as `onOpenProfile`
// here (without pulling AgentNode's React tree into a server-spawn path).

function AgentNodeImpl({ data }: NodeProps) {
  const t = useT();
  const itemId = (data as AgentNodeData).itemId;
  const item = useWorkspaceItem(itemId);
  const workspace = useWorkspace();
  const tabId = useWorkspaceTabId();
  const { patchItemState, spawnFromAgent, findFirstByRef, bringToFront } =
    useWorkspaceActions();
  // Mounted unconditionally — the hook is a no-op until events arrive.
  // `rootHostingAgentItemId` is resolved below from cache; mount AFTER
  // it's available so the hook closes over the correct tag value.
  const flow = useReactFlow();

  // Header double-click brings this window into view: framed at 100% through
  // the canvas's `ws:focus-item` chokepoint, like the taskbar. It used to
  // `fitView` onto the node, which zoomed the canvas so the node filled it,
  // and workspace zoom is disabled (2026-09-26). Before that it opened a
  // full page; per user feedback it keeps the user in the workspace.
  const fitWidgetToScreen = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent('ws:focus-item', { detail: { itemId } }),
    );
  }, [itemId]);

  const username = (item?.state.cache?.username as string | undefined) ?? '';
  const displayName = (item?.state.cache?.displayName as string | undefined) ?? username;
  const avatarUrl = (item?.state.cache?.avatarUrl as string | null | undefined) ?? null;

  /* HEAL A DISPLAY NAME THE CACHE GOT WRONG (2026-09-16 browser pass).
     `cache.displayName` is written once, by whichever path spawned the node,
     and nothing has ever invalidated it. An agent renamed after that keeps the
     old name on the canvas forever, because this node never reads the row
     again.

     Found on the per-user app agents of ADR 0042: `ensureAppAgent` heals rows
     whose displayName is the raw wire slug, and its own comment says the
     lowercase value "shows on the canvas node, the rail tooltip and the rail
     label". It updates `users.displayName`, which is the half this component
     does not read, so the heal landed in the database and the canvas went on
     saying `revent` under `@robutlerai.revent` while the chat messages inside
     that same node said "Revent". A person reading their own workspace saw the
     internal identifier.

     `resolveParticipant` is de-duped and process-cached, so this is one fetch
     per handle per session however many nodes are open, and the patch only
     fires on a real difference, so it converges on the first pass and writes
     nothing on later ones. Display name only: the avatar is deliberately
     absent on these agents (ADR 0042 decision 4) and is not this defect. */
  useEffect(() => {
    if (!itemId || !username || !displayName) return;
    let cancelled = false;
    void (async () => {
      const live = await resolveParticipant(username).catch(() => null);
      if (cancelled || !live?.displayName || live.displayName === displayName) return;
      patchItemState(itemId, {
        cache: { ...(item?.state.cache || {}), displayName: live.displayName },
      });
    })();
    return () => { cancelled = true; };
    // `item.state.cache` is read inside on purpose: it must not re-run the
    // fetch every time an unrelated cache key changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, username, displayName, patchItemState]);

  const chatId = item?.state.chatId;
  const view = ((item?.state.view as View | undefined) ?? 'welcome') as View;
  // When the agent has a custom UI, `chatMode` makes the welcome view render
  // the built-in chat (greeting + active chat) instead of the widget.
  const chatMode = !!item?.state.chatMode;
  const docked = item ? getPlacement(item.state) === 'docked' : false;
  const collapsed = !!item?.state.collapsed;
  // The same widget type hosts both AI agents AND humans. `cache.accountType`
  // distinguishes them; older items (pre-generalisation) have no `accountType`
  // and default to `'agent'` — this matches the pre-2026-05 behaviour where the
  // widget rendered agent-only chrome unconditionally. Read-only fallback; we
  // don't backfill via a migration to keep the storage layer simple.
  //
  // The value is a `users.account_type` enum member, which is what
  // `/api/users/<handle>` returns and what every spawn path writes.
  // `isHumanParticipant` owns the comparison — see the note there on
  // why it is not inlined any more.
  const accountType =
    ((item?.state.cache?.accountType as string | undefined) ?? 'agent');
  const isHuman = isHumanParticipant(accountType);

  // --- Custom UI widget (owner-gated) -------------------------------
  // An agent's `welcome` body can be an owner-chosen widget instead of the
  // built-in chat pane. Phase 1 is OWNER-ONLY (confused-deputy guard): the
  // `GET /api/agents/:id` read is owner-scoped (404s for non-owners), and we
  // additionally require `featureFlags.customUI` + a declared `metadata.ui`.
  // The widget connects to its agent via `host.agents` (the bridge seeds the
  // grant from `connectedAgentId`); resolution: metadata.ui → built-in welcome.
  const { user: currentUser } = useAuth();
  const agentRefId = item?.refId ?? undefined;
  const [uiWidget, setUiWidget] = useState<{ spec: WidgetSpec; external: boolean } | null>(null);
  // For an OWNER viewing a `transport:'local'` agent: the spec of the hidden
  // on-device compute runner to mount (so the agent is online + answerable).
  const [localRunnerSpec, setLocalRunnerSpec] = useState<WidgetSpec | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (accountType !== 'agent' || !agentRefId || !currentUser?.id) {
      setUiWidget(null);
      setLocalRunnerSpec(null);
      return;
    }
    void (async () => {
      if (!cancelled) setLocalRunnerSpec(null);
      try {
        const res = await fetch(`/api/agents/${encodeURIComponent(agentRefId)}`, {
          credentials: 'same-origin',
        });
        if (!res.ok) {
          if (!cancelled) setUiWidget(null); // 404 ⇒ not owner ⇒ welcome
          return;
        }
        const body = (await res.json()) as {
          agent?: { ownerId?: string | null };
          config?: {
            featureFlags?: Record<string, unknown> | null;
            metadata?: {
              ui?: {
                widgetType?: string;
                url?: string;
                initialKv?: Record<string, unknown>;
                // Plan v3-03 Part C — a remixable folder/bundle widget
                // (e.g. a remixed voice). `bundle` is the resolved
                // WidgetBundle snapshot persisted by the ui-config route.
                contentId?: string;
                bundle?: Record<string, unknown>;
                size?: { width?: number; height?: number };
              };
            } | null;
          } | null;
          // For a `transport:'local'` agent the server resolves the
          // on-device runner's self-contained bundle so it renders from
          // the content store (not the shared `/widgets/agent-runner`).
          runnerBundle?: {
            bundle?: Record<string, unknown>;
            size?: { width?: number; height?: number };
          } | null;
        };
        const isOwner = body.agent?.ownerId === currentUser.id;
        // A folder bundle only renders correctly through the sandbox SUBDOMAIN
        // (distinct origin → inner iframe gets `allow-same-origin`). Without a
        // configured `SANDBOX_ORIGIN` (e.g. local dev) the inner iframe is
        // opaque and its `'self'` resources are CSP-blocked — so there we must
        // use the static `widgetType` render instead.
        const canSandbox = !!getRuntimeConfig().sandboxOrigin;
        // An OWNER viewing a `transport:'local'` agent mounts the hidden
        // on-device compute runner so the agent's brain is served from THIS
        // browser (and the agent appears online). Independent of the custom-UI
        // resolution below.
        const cfgModel = typeof (body.config as { model?: string } | null)?.model === 'string'
          ? ((body.config as { model?: string }).model as string)
          : '';
        if (!cancelled) {
          // Prefer the server-resolved self-contained bundle, but ONLY when a
          // distinct sandbox origin is configured — a folder bundle rendered
          // same-origin (no `SANDBOX_ORIGIN`, e.g. local) gets an opaque inner
          // iframe whose `style-src/script-src 'self'` block the bundle's own
          // files. There, fall back to the static `/widgets/agent-runner`
          // render (same-origin, works). See `resolveBundleSpec`.
          const runnerMeta =
            body.runnerBundle?.bundle && canSandbox
              ? { bundle: body.runnerBundle.bundle, size: body.runnerBundle.size }
              : { widgetType: 'agent-runner' };
          setLocalRunnerSpec(
            isOwner && getModelTransport(cfgModel) === 'local'
              ? resolveWidget(runnerMeta)
              : null,
          );
        }
        // A custom UI shows for the OWNER when `metadata.ui` is set. The
        // per-agent `featureFlags.customUI === false` is an explicit
        // kill-switch (default on) so a UI can be disabled without clearing
        // the chosen widget.
        const disabled = body.config?.featureFlags?.customUI === false;
        const ui = body.config?.metadata?.ui;
        if (!isOwner || disabled || !ui || typeof ui !== 'object') {
          if (!cancelled) setUiWidget(null);
          return;
        }
        // Prefer the self-contained bundle when the sandbox can host it (or
        // when there's no static fallback, e.g. a remix). Otherwise fall back
        // to the static `widgetType` render — the canonical agent UI then
        // renders same-origin from `/widgets/<type>` (works without a sandbox
        // origin). See `canSandbox` above.
        const meta =
          ui.bundle && (canSandbox || !ui.widgetType)
            ? { bundle: ui.bundle, size: ui.size, ...(ui.widgetType ? { widgetType: ui.widgetType } : {}) }
            : ui.widgetType
              ? { widgetType: ui.widgetType }
              : ui.url
                ? { iframeEntry: ui.url }
                : null;
        const spec = meta ? resolveWidget(meta) : null;
        if (!spec || !spec.entry) {
          if (!cancelled) setUiWidget(null);
          return;
        }
        if (!cancelled) setUiWidget({ spec, external: spec.kind === 'iframe-external' });
      } catch {
        if (!cancelled) setUiWidget(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `view` is a dep so returning to the welcome view after picking a UI in
    // settings re-reads `metadata.ui` (the settings panel PATCHes the agent on
    // its own; this node otherwise wouldn't see the change until remount).
  }, [accountType, agentRefId, currentUser?.id, view]);

  // Toggle a custom-UI agent between its widget and the built-in chat. When
  // ENTERING chat with no chat bound, resolve the caller↔agent canonical 1:1 DM
  // (the thread the voice widget persists to) and bind it, so the chat shows
  // that conversation instead of an empty draft. This is tied to the toggle
  // ACTION — not a passive effect — so the "New chat" action (which also clears
  // chatId) is NOT hijacked back to the DM. Find-only: a brand-new agent with
  // no DM stays in the draft greeting.
  const toggleChatMode = useCallback(() => {
    const entering = !chatMode;
    patchItemState(itemId, { chatMode: entering, view: 'welcome' });
    if (!entering || !uiWidget || accountType !== 'agent' || chatId || !agentRefId || !currentUser?.id) return;
    void (async () => {
      try {
        const res = await fetch('/api/messages/find', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ participantIds: [agentRefId] }),
        });
        if (!res.ok) return;
        const data = (await res.json()) as { chats?: Array<{ id?: string; participants?: Array<{ userId?: string; id?: string }> }> };
        const chats = Array.isArray(data?.chats) ? data.chats : [];
        // The 1:1 DM = a ≤2-participant chat that includes this agent.
        const dm = chats.find((c) => {
          const ps = Array.isArray(c?.participants) ? c.participants : [];
          return ps.length <= 2 && ps.some((p) => (p?.userId ?? p?.id) === agentRefId);
        });
        if (dm?.id) patchItemState(itemId, { chatId: dm.id });
      } catch {
        /* best-effort — voice still falls back to the server DM */
      }
    })();
  }, [chatMode, uiWidget, accountType, chatId, agentRefId, currentUser?.id, itemId, patchItemState]);

  // "New chat" — create a genuinely NEW thread and switch the active chat to it
  // so BOTH the chat view and the voice view follow (voice binds to the active
  // chatId). POST /api/messages always creates a new chat (agents support
  // multiple conversations), unlike the find-or-create DM the voice fallback
  // uses — so without binding the new id, voice would keep writing to the old
  // DM. Falls back to a draft if the create fails.
  const startNewChat = useCallback(() => {
    if (!agentRefId) {
      patchItemState(itemId, { chatId: null, view: 'welcome' });
      return;
    }
    void (async () => {
      try {
        const res = await fetch('/api/messages', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ type: 'dm', participantIds: [agentRefId] }),
        });
        if (res.ok) {
          const data = (await res.json()) as { chatId?: string };
          if (data?.chatId) {
            patchItemState(itemId, { chatId: data.chatId, view: 'welcome' });
            return;
          }
        }
      } catch {
        /* fall through to a draft */
      }
      patchItemState(itemId, { chatId: null, view: 'welcome' });
    })();
  }, [agentRefId, itemId, patchItemState]);
  // Inherited when this agent is itself a delegation child; otherwise the
  // root of any chain is *this* widget. The hook in section 3 uses this
  // to tag spawned pucks so an entire chain can be torn down on the
  // next outer-level delegation start.
  const rootHostingAgentItemId =
    (item?.state.cache?.delegationOriginAgentItemId as string | undefined) ?? itemId;

  useDelegationVisualization({
    hostingAgentItemId: itemId,
    rootHostingAgentItemId,
  });

  /**
   * Tapping the prominent avatar in the header collapses the widget
   * down to an avatar-puck (the size the agent shows in lists). Tap
   * again to restore the full window.
   *
   * Geometry — the collapsed bbox wraps the 56×56 avatar exactly
   * (plus 4px ring buffer = 64×64). That tight bbox is what lets
   * React Flow's connector handles land flush on the avatar's
   * silhouette instead of floating somewhere over an invisible
   * 160×112 puck rectangle. The name + handle label rendered below
   * the avatar (see NodeFrame) is `position: absolute` outside the
   * bbox, so it stays visible without enlarging the connector hit
   * area.
   *
   * We snapshot the expanded width/height to `state.expandedSize`
   * so the expand gesture restores the user's tuned dimensions
   * exactly. Position is adjusted alongside so the avatar's screen
   * centre stays put across the collapse/expand toggle — without
   * the shift, the avatar would jump as the bbox shrinks toward
   * the top-left corner and the expanded layout's negative offset
   * vanishes.
   *
   * The transition class `.ws-collapse-anim` flips a CSS
   * width/height transition on the React Flow node wrapper for the
   * duration of the animation only — leaving manual edge-resize
   * gestures untouched the rest of the time.
   */
  // Magic numbers calibrated to the NodeFrame layout. The 56px avatar
  // is wrapped in an absolute div positioned at
  // `-left-1 -top-2 -translate-x-2` — Tailwind defaults of
  // `1 = 4px`, `2 = 8px` resolve that to (-12, -8) relative to the
  // bbox top-left. The avatar's CENTRE therefore sits at
  // (-12 + 28, -8 + 28) = (+16, +20).
  //
  // Collapsed, the avatar is centred in the TOP 64x64 band of a
  // 64x92 bbox (the bottom 28px holds the under-avatar @username
  // label). Avatar centre therefore stays at (+32, +32). Keeping
  // the on-screen avatar centre fixed across the toggle requires
  // shifting the bbox top-left by (-16, -12) on collapse and
  // (+16, +12) on expand — the extra 28px of bbox height extends
  // DOWN below the avatar, so the avatar centre is unaffected.
  const COLLAPSED_W = 64;
  const COLLAPSED_TITLE_H = 28;
  const COLLAPSED_H = COLLAPSED_W + COLLAPSED_TITLE_H; // 92
  const COLLAPSED_DX = 16;
  const COLLAPSED_DY = 12;

  const toggleCollapsed = () => {
    if (!item) return;
    if (collapsed) {
      const restored = (item.state.expandedSize as { width: number; height: number } | undefined) ?? {
        width: 410,
        height: 580,
      };
      // Delegation pucks expand into the live sub-conversation: when
      // `cache.delegationSubChatId` is known and the widget hasn't
      // already been bound to a chat, snap `chatId` over so ChatView
      // renders the populated chat instead of the agent's welcome
      // screen. Without this the prompt input floats high inside an
      // empty widget — the welcome-state layout pushes it up to make
      // room for the suggested-prompt buttons.
      const subChatId =
        (item.state.cache?.delegationSubChatId as string | undefined) ?? null;
      const nextChatId =
        item.state.chatId ?? (subChatId && subChatId.length > 0 ? subChatId : null);
      patchItemState(itemId, {
        collapsed: false,
        x: item.state.x + COLLAPSED_DX,
        y: item.state.y + COLLAPSED_DY,
        width: restored.width,
        height: restored.height,
        ...(nextChatId !== item.state.chatId ? { chatId: nextChatId } : {}),
      });
    } else {
      patchItemState(itemId, {
        collapsed: true,
        expandedSize: { width: item.state.width, height: item.state.height },
        x: item.state.x - COLLAPSED_DX,
        y: item.state.y - COLLAPSED_DY,
        width: COLLAPSED_W,
        height: COLLAPSED_H,
      });
    }
    // Flip a transient class on the underlying RF node so the size
    // change tweens. Removed after the transition completes so
    // ongoing edge-resize stays snappy.
    if (typeof window !== 'undefined') {
      requestAnimationFrame(() => {
        const el = document.querySelector(
          `.react-flow__node[data-id="${itemId}"]`,
        );
        if (!el) return;
        el.classList.add('ws-collapse-anim');
        window.setTimeout(() => el.classList.remove('ws-collapse-anim'), 360);
      });
    }
  };

  // Dedupe spawn intents so a chat that emits the same delta multiple times
  // (or two AgentNodes briefly mounted with the same chatId during HMR) only
  // ever produces one widget per (type, refId) pair.
  //
  // Two layers of dedup:
  //   1. `spawnedRef` — in-memory set, suppresses duplicate spawn events
  //      within THIS AgentNode's lifetime (HMR / fast-second-delta).
  //   2. `findFirstByRef(refId)` — STORE check, suppresses spawns that
  //      duplicate an already-on-canvas item. This is what prevents the
  //      "agent edits widget → second widget appears" bug: agent
  //      `text_editor str_replace` keeps the same content_id, the
  //      tool_result fires a present-event for that content_id, and
  //      without this check we'd add a second canvas item pointing at
  //      the same row (you saw "purple snake top, blue snake bottom").
  //      When the existing item is found we focus + bring-to-front
  //      instead — combined with the `workspace:content-updated`
  //      window event (see chat-view.tsx tool_result handler) the
  //      iframe re-renders with the new code at the existing position.
  const spawnedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!chatId) return;
    const unsub = subscribeWorkspaceSpawn((event) => {
      if (event.chatId !== chatId) return;
      if (event.type !== 'content' && event.type !== 'agent') return;
      const key = `${event.type}:${event.refId}`;
      if (spawnedRef.current.has(key)) return;
      spawnedRef.current.add(key);

      // Layer 2 dedup: does the canvas already have a non-docked item
      // with this refId? Focus it instead of spawning a duplicate.
      const existing = findFirstByRef(event.refId);
      if (existing) {
        bringToFront(existing.id);
        const cx = existing.state.x + existing.state.width / 2;
        const cy = existing.state.y + existing.state.height / 2;
        try {
          flow.setCenter(cx, cy, { duration: 280, zoom: flow.getZoom() });
        } catch {
          // RF not yet measured — silent fallback.
        }
        return;
      }

      spawnFromAgent({
        parentItemId: itemId,
        type: event.type,
        refId: event.refId,
        state: { cache: event.cache },
      })
        .then((spawned) => {
          if (!spawned) return;
          const cx = spawned.state.x + spawned.state.width / 2;
          const cy = spawned.state.y + spawned.state.height / 2;
          try {
            flow.setCenter(cx, cy, { duration: 280, zoom: flow.getZoom() });
          } catch {
            // RF not yet measured — silent fallback.
          }
        })
        .catch(() => {
          spawnedRef.current.delete(key);
        });
    });
    return unsub;
  }, [chatId, itemId, spawnFromAgent, findFirstByRef, bringToFront, flow]);

  /**
   * Every state button toggles back to the welcome state (which IS
   * the chat experience — draft greeting or active chat depending on
   * `chatId`). Welcome is the resting state, so when a button is
   * already active a second click returns to it.
   */
  const toggleView = (next: Exclude<View, 'welcome'>) => {
    if (view === next) {
      patchItemState(itemId, { view: 'welcome' });
    } else {
      patchItemState(itemId, { view: next });
    }
  };

  const openChat = (id: string) => {
    // Picking a chat from the list saves it on the widget AND drops
    // back to the welcome (chat) state — the user is now "in" that
    // chat. Persisted so reload / panel-toggle returns here.
    patchItemState(itemId, { chatId: id, view: 'welcome' });
  };

  /**
   * Per the plan §3f, deeper chains are truncated. 5 levels covers
   * every observed real-world delegation tree we have telemetry for
   * (the largest measured is 3), with headroom for "agent of agents"
   * fan-out without runaway client work or stale tool-call traversal.
   */
  const MAX_REPLAY_DEPTH = 5;
  /**
   * Debounce window for `onReplayDelegation` — repeated clicks within
   * 200ms collapse to a single replay. Without this, double-clicks
   * (common on touch) trigger two concurrent replays that race on the
   * tear-down + spawn sequence inside `useDelegationVisualization`.
   */
  const lastReplayAtRef = useRef(0);

  /**
   * `@username` mention click inside chat → spawn (or focus) a sibling
   * participant widget to the right of this agent. The same
   * `type: 'agent'` widget hosts both AI agents AND humans; the renderer
   * branches on `cache.accountType` to hide agent-only tabs (settings,
   * model picker) for humans. See `ChatHostHandlers.onOpenProfile` in
   * `components/chat/chat-host-context.tsx`.
   *
   * Tiny in-memory cache: the same `/u/<handle>` link can be hit dozens
   * of times during a single chat session (delegation chains repeat
   * usernames in tool-call rows + summary text). One fetch per session
   * per handle is plenty.
   */
  /**
   * Click on a historical `delegate` tool-call row → re-emit the
   * synthetic delegation events so `useDelegationVisualization` rebuilds
   * the chain on the canvas. Walks the persisted sub-chat tree to
   * reconstruct nested levels up to `MAX_REPLAY_DEPTH`.
   *
   * All replayed events use `phase: 'complete'` because we already know
   * how each historical delegation ended — we never emit a `start` after
   * a `complete`, which keeps the edge animation from flickering live →
   * done → live as the recursion unfolds.
   *
   * The hook itself owns the tear-down + spawn sequencing; this handler
   * just emits the events.
   */
  const onReplayDelegation = useCallback<NonNullable<ChatHostHandlers['onReplayDelegation']>>(
    (toolCall) => {
      const now = Date.now();
      if (now - lastReplayAtRef.current < 200) return true;
      lastReplayAtRef.current = now;

      void (async () => {
        /**
         * Historical `delegate` rows are persisted with various arg
         * keys depending on which surface authored them (web NLI uses
         * `agent`; MCP / external delegate uses `agent_url` /
         * `agent_identifier`). Some rows have no useful args at all
         * (older callers, partial persistence) but every one carries
         * `data.subChatId`. We treat sub-chat resolution as the
         * primary identity source and only fall back to args if the
         * sub-chat lookup yields nothing.
         */
        const parseArgs = (raw: unknown): { agent?: string } => {
          const obj: Record<string, unknown> | null = (() => {
            if (typeof raw === 'string') {
              try { return JSON.parse(raw) as Record<string, unknown>; }
              catch { return null; }
            }
            if (raw && typeof raw === 'object') return raw as Record<string, unknown>;
            return null;
          })();
          if (!obj) return {};
          const agent = (obj.agent ?? obj.agent_url ?? obj.agent_identifier) as string | undefined;
          return agent ? { agent } : {};
        };

        /**
         * Last-resort recovery: ask the parent chat for the `role='tool'`
         * row matching this `callId` and read `subChatId` off its
         * `metadata.tool_data`. We hit this when (a) the chip was clicked
         * before the live overlay's `tool_result` delta arrived (so
         * `data.subChatId` is undefined client-side) AND (b) `args`
         * didn't yield a usable agent handle. Covers the streaming-race
         * symptom the user filed where the chip click logs
         * "could not resolve delegated agent" despite the delegation
         * having actually completed server-side.
         *
         * Cached per (parentChatId, callId) on the global resolver
         * cache so a chat with several delegate rounds only fetches
         * once per click burst.
         */
        const subChatIdFromPersisted = async (
          parentChatId: string | undefined,
          callId: string,
        ): Promise<string | undefined> => {
          if (!parentChatId) return undefined;
          try {
            const res = await fetch(`/api/messages/${parentChatId}?limit=100`);
            if (!res.ok) return undefined;
            const body = (await res.json()) as {
              messages?: Array<{
                role?: string;
                toolCallId?: string | null;
                metadata?: { tool_data?: { subChatId?: string } } | null;
              }>;
            };
            for (const msg of body.messages ?? []) {
              if (msg.role !== 'tool') continue;
              if (msg.toolCallId !== callId) continue;
              const sid = msg.metadata?.tool_data?.subChatId;
              if (typeof sid === 'string' && sid.length > 0) return sid;
            }
            return undefined;
          } catch {
            return undefined;
          }
        };

        // Emits one synthetic delegation chain, then recursively
        // descends through any persisted `delegate` tool-calls inside
        // the sub-chat. `hostingItemId` rotates as we descend: a level
        // deeper, the puck spawned for the previous level BECOMES the
        // host. We approximate that by re-using `rootHostingAgentItemId`
        // so the tagged origin stays consistent (a true per-level host
        // would require waiting for each spawn to land before emitting
        // the next start — significant complexity for a v1 replay).
        const replay = async (
          tc: {
            callId: string;
            args: unknown;
            data?: { subChatId?: string };
            parentChatId?: string;
          },
          depth: number,
          parentHostingItemId: string,
        ): Promise<void> => {
          if (depth >= MAX_REPLAY_DEPTH) return;

          // Primary identity source: ask the sub-chat itself who the
          // callee is. This works for every delegate row that managed
          // to create a sub-chat, regardless of which arg shape the
          // caller persisted (or whether it persisted any args at all).
          let resolvedId: string | undefined;
          let resolvedUsername: string | undefined;
          let resolvedDisplayName: string | undefined;
          let resolvedAvatarUrl: string | undefined;
          let resolvedSubChatId: string | undefined = tc.data?.subChatId;
          let resolveSource: 'subchat' | 'args' | 'persisted' | 'none' = 'none';
          if (resolvedSubChatId) {
            const fromSubChat = await resolveDelegatedAgentFromSubChat(resolvedSubChatId);
            if (fromSubChat) {
              resolvedId = fromSubChat.id;
              resolvedUsername = fromSubChat.username;
              resolvedDisplayName = fromSubChat.displayName;
              resolvedAvatarUrl = fromSubChat.avatarUrl;
              resolveSource = 'subchat';
            }
          }

          // Fallback 1: scrape the username from args. Covers rows
          // whose sub-chat is missing (deleted, or replay-from-args-
          // only) and the live `start` event path that fires before
          // the sub-chat is reachable.
          if (!resolvedUsername) {
            const parsed = parseArgs(tc.args);
            const username = (parsed.agent ?? '').replace(/^@/, '');
            if (username) {
              const cached = await resolveParticipant(username);
              resolvedUsername = username;
              resolvedId = cached?.id;
              resolvedDisplayName = cached?.displayName ?? undefined;
              resolvedAvatarUrl = cached?.avatarUrl ?? undefined;
              resolveSource = 'args';
            }
          }

          // Fallback 2: persisted `tool_data.subChatId` from the
          // parent chat's `role='tool'` row matching this callId.
          // Catches the streaming-race case where the live overlay
          // delivered a `tool_call` but not the `tool_result` data,
          // AND `args` parsing didn't find an agent handle (e.g.
          // malformed JSON mid-stream). The server already wrote the
          // sub-chat id on the tool turn — we just have to fetch it.
          if (!resolvedUsername && tc.parentChatId) {
            const recovered = await subChatIdFromPersisted(
              tc.parentChatId,
              tc.callId,
            );
            if (recovered) {
              const fromSubChat = await resolveDelegatedAgentFromSubChat(recovered);
              if (fromSubChat) {
                resolvedId = fromSubChat.id;
                resolvedUsername = fromSubChat.username;
                resolvedDisplayName = fromSubChat.displayName;
                resolvedAvatarUrl = fromSubChat.avatarUrl;
                resolvedSubChatId = recovered;
                resolveSource = 'persisted';
              }
            }
          }

          if (!resolvedUsername) {
            // eslint-disable-next-line no-console
            console.warn(
              '[agent-node] replay: could not resolve delegated agent',
              {
                callId: tc.callId,
                subChatId: tc.data?.subChatId,
                parentChatId: tc.parentChatId,
                hasArgs: !!tc.args,
                argsType: typeof tc.args,
                argsPreview:
                  typeof tc.args === 'string'
                    ? tc.args.slice(0, 200)
                    : tc.args,
              },
            );
            return;
          }
          // eslint-disable-next-line no-console
          console.debug('[agent-node] replay: resolved', {
            callId: tc.callId,
            username: resolvedUsername,
            via: resolveSource,
            depth,
          });
          emitDelegationEvent({
            phase: 'start',
            callId: tc.callId,
            hostingAgentItemId: parentHostingItemId,
            rootHostingAgentItemId,
            delegated: {
              username: resolvedUsername,
              id: resolvedId,
              displayName: resolvedDisplayName,
              avatarUrl: resolvedAvatarUrl,
            },
            // Use the RECOVERED sub-chat id — the chip's raw
            // `tc.data?.subChatId` is often absent for historical
            // delegations, but we've already resolved it via args /
            // persisted tool_data above. Without this the puck
            // spawns without a `chatId`, and expanding lands on the
            // agent's welcome screen instead of the delegated
            // sub-conversation.
            subChatId: resolvedSubChatId,
            // Mark as replay — the hook skips its `seenCallIds`
            // dedupe so the same historical call can be opened over
            // and over from chat history, and treats the event as an
            // explicit "open this delegation on the canvas" intent
            // (replacing any sibling delegations under this root).
            isReplay: true,
          });
          // No follow-up `complete` event on replay. The hook's
          // `start` handler now lands the puck directly in
          // `delegationStatus: 'complete'` with `chatId` /
          // `delegationCompletedAt` all pre-bound, so a second patch
          // here would only re-bump `delegationCompletedAt` and
          // cause a visible re-render flicker right after the spawn
          // animation. Live (non-replay) delegations still emit
          // their own `complete` from the streaming runtime.

          // Recurse via sub-chat. Best-effort — a failure (404, network,
          // permissions) is logged and we render the chain partially.
          // Use whichever sub-chat id we ended up with (live data,
          // persisted recovery, …) so a recovered chain can still walk
          // its children.
          const recurseSubChatId = resolvedSubChatId;
          if (!recurseSubChatId) return;
          try {
            const res = await fetch(`/api/messages/${recurseSubChatId}?limit=100`);
            if (!res.ok) return;
            const body = (await res.json()) as {
              messages?: Array<{ contentItems?: Array<{ type?: string; tool_call?: { id?: string; name?: string; arguments?: unknown }; data?: unknown }> }>;
            };
            for (const msg of body.messages ?? []) {
              for (const ci of msg.contentItems ?? []) {
                if (ci.type !== 'tool_call') continue;
                const tcName = ci.tool_call?.name?.replace(/_tool$/, '');
                if (tcName !== 'delegate' && tcName !== 'nli') continue;
                const childId = ci.tool_call?.id;
                if (!childId) continue;
                await replay(
                  {
                    callId: childId,
                    args: ci.tool_call?.arguments,
                    data: ci.data as { subChatId?: string } | undefined,
                    // Children of this delegation live in the sub-chat
                    // we just walked, so that's the parent chat id for
                    // their own persisted-tool-data recovery path.
                    parentChatId: recurseSubChatId,
                  },
                  depth + 1,
                  // The previous-level puck IS the host for grandchildren.
                  // Without a stable id at this point (the spawn hasn't
                  // landed yet) we re-host on `rootHostingAgentItemId`,
                  // which produces a flatter visualisation than a true
                  // tree but keeps the lineage tag consistent.
                  rootHostingAgentItemId,
                );
              }
            }
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[agent-node] replay sub-chat fetch failed', err);
          }
        };

        await replay(
          {
            callId: toolCall.callId,
            args: toolCall.args,
            data: toolCall.data,
            parentChatId: toolCall.chatId,
          },
          0,
          itemId,
        );
      })();

      return true;
    },
    [itemId, rootHostingAgentItemId],
  );

  const onOpenProfile = useCallback(
    (handle: string) => {
      void (async () => {
        const resolved = getCachedParticipant(handle) ?? (await resolveParticipant(handle));
        if (!resolved) return;
        const existing = findFirstByRef(resolved.id);
        if (existing) {
          bringToFront(existing.id);
          try {
            flow.setCenter(
              existing.state.x + existing.state.width / 2,
              existing.state.y + existing.state.height / 2,
              { duration: 280, zoom: flow.getZoom() },
            );
          } catch {
            // RF not yet measured; ignore.
          }
          return;
        }
        void spawnFromAgent({
          parentItemId: itemId,
          type: 'agent',
          refId: resolved.id,
          state: {
            cache: {
              username: resolved.username,
              // `resolveParticipant` returns `string | null` for
              // optional identity fields; the workspace cache prefers
              // `undefined` so the JSON column doesn't carry literal
              // nulls for missing values.
              displayName: resolved.displayName ?? undefined,
              avatarUrl: resolved.avatarUrl ?? undefined,
              accountType: resolved.accountType,
              ownerId: resolved.ownerId ?? undefined,
            },
          },
        });
      })();
      // Claim the click synchronously — see HostAwareLink's contract.
      return true;
    },
    [itemId, spawnFromAgent, findFirstByRef, bringToFront, flow],
  );

  // Register this agent widget with the workspace command bus so
  // OTHER agents on the canvas can drive it through
  // `workspace_widgets_invoke(itemId, 'chat' | 'state' | 'message.send')`.
  // `chat` is the agent-loop entry point (token streaming, tool calls,
  // etc.) and remains unwired until that surface grows a non-UI entry
  // point; `message.send` is the simpler "send a DM" surface that
  // routes through the same `/api/messages/...` path the user UI uses
  // and works uniformly for human OR agent participants.
  //
  // `message.received` is a push-only event: agents subscribe ahead of
  // sending so they can correlate replies. The subscriber set is held
  // in a ref so the registration value (a useMemo result) stays stable
  // across UAMP event arrivals — without the ref, every push would tear
  // down and re-register the widget.
  const messageSubscribers = useRef(new Set<(payload: unknown) => void>());
  const uampApi = useUAMP();
  useEffect(() => {
    if (!chatId) return;
    const off = uampApi.on('chat:message', (payload) => {
      // Filter strictly to this widget's active chat. We pass through
      // BOTH inbound and outbound messages so subscribers (typically
      // an external agent driving the conversation) can reconcile
      // delivery acks; the subscriber can drop self-sent messages by
      // inspecting `senderId` if needed.
      const msg = payload as { chatId?: string };
      if (msg.chatId !== chatId) return;
      for (const fn of messageSubscribers.current) {
        try {
          fn(payload);
        } catch {
          // Defensive — one misbehaving subscriber must not break
          // delivery to the others.
        }
      }
    });
    return off;
  }, [chatId, uampApi]);

  const agentSpec = WIDGET_REGISTRY.agent;
  const agentInterface = agentSpec?.interface;
  const registration = useMemo(() => {
    // When a custom UI widget is mounted in the body, the embedded
    // `WidgetBridge` owns this item's `WidgetCommandBus` registration (the UI
    // widget becomes the agent's command surface). Skipping here avoids a
    // double-registration on the same `itemId`.
    if (!agentSpec || !agentInterface || !item || uiWidget) return null;
    return {
      itemId,
      widgetType: 'agent',
      spec: agentSpec,
      interface: agentInterface,
      title: displayName || username || 'Agent',
      item,
      invoke: async (name: string, args: unknown) => {
        switch (name) {
          case 'state':
            return {
              chatId: chatId ?? null,
              username,
              displayName,
              messages: [],
            };
          case 'chat':
            throw new BusError(
              'internal',
              'agent.chat dispatch is not wired through the bus yet — ' +
                'send the message via /api/chats and pin the chatId on ' +
                'this widget instead.',
            );
          case 'message.send': {
            // Lightweight wrapper around the existing `/api/messages`
            // POST. Auth + participant checks happen server-side via
            // the request cookies (same path the chat UI uses), so an
            // agent driving this through the bus is constrained to the
            // current user's identity — no privilege escalation.
            const payload = (args ?? {}) as {
              text?: unknown;
              attachments?: unknown;
            };
            if (typeof payload.text !== 'string' || payload.text.length === 0) {
              throw new BusError('invalid_args', 'message.send requires { text: string }');
            }
            if (!chatId) {
              // The participant widget has no chat yet (welcome state).
              // Creating one synchronously is possible via
              // /api/messages/create-with-message, but it has more
              // edge cases (sets `chatId` on this widget) than v1 of
              // the bus surface wants to handle.
              throw new BusError(
                'internal',
                'message.send needs an active chatId; start a chat in the UI first',
              );
            }
            const res = await fetch(`/api/messages/${chatId}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                content: payload.text,
                contentItems: Array.isArray(payload.attachments)
                  ? payload.attachments
                  : undefined,
              }),
            });
            if (!res.ok) {
              const text = await res.text().catch(() => '');
              throw new BusError('internal', text || `message.send failed: ${res.status}`);
            }
            const body = (await res.json()) as { message?: { id?: string } };
            return { messageId: body.message?.id ?? '' };
          }
          case 'setTab':
            throw new BusError(
              'unknown_op',
              'agent.setTab is owned by the user UI; agents should not toggle it',
            );
          case 'focusChat':
            throw new BusError(
              'unknown_op',
              'agent.focusChat is owned by the user UI; agents should not toggle it',
            );
          default:
            throw new BusError('unknown_op', `agent widget: unknown command '${name}'`);
        }
      },
      subscribe: (eventName: string, onPush: (payload: unknown) => void) => {
        // Only `message.received` is push-driven from inside AgentNode
        // today. Other declared events (`message`, `status`,
        // `delegated`, `chatStarted`) require ChatView-level hooks
        // we don't have a stable surface for yet — return a no-op
        // unsubscriber so subscriptions don't error, mirroring the
        // bus's "declared but inert" pattern.
        if (eventName !== 'message.received') {
          return () => {};
        }
        messageSubscribers.current.add(onPush);
        return () => {
          messageSubscribers.current.delete(onPush);
        };
      },
    };
  }, [agentSpec, agentInterface, item, itemId, chatId, displayName, username, uiWidget]);
  useRegisterWidget(registration);

  const actions: NodeFrameAction[] = useMemo(() => {
    const base: NodeFrameAction[] = [
      {
        id: 'chats',
        label: 'Chats',
        tone: 'blue' as const,
        icon: MessagesSquare,
        onClick: () => toggleView('chats'),
        active: view === 'chats',
      },
    ];
    // Custom-UI ⇄ Chat toggle — only when the agent declares a custom UI.
    // Switches the welcome view between the widget and the built-in chat
    // (greeting + active chat). The icon/label name the TARGET mode.
    if (uiWidget) {
      base.push({
        id: 'chatmode',
        label: chatMode ? t('workspace.node.customUi') : t('workspace.canvas.node.chat'),
        tone: 'purple' as const,
        icon: chatMode ? LayoutDashboard : MessageCircle,
        onClick: toggleChatMode,
        active: false,
      });
    }
    // Settings is an agent-only concept (system prompt, model, tools,
    // integrations). For human participants the same panel is meaningless
    // and exposes nothing the human can change about themselves from a
    // peer's workspace — drop it entirely instead of rendering a stub.
    if (!isHuman) {
      base.push({
        id: 'settings',
        label: 'Settings',
        tone: 'slate' as const,
        icon: Settings,
        onClick: () => toggleView('settings'),
        active: view === 'settings',
      });
    }
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, isHuman, uiWidget, chatMode, itemId, toggleChatMode]);

  if (!item) return null;

  const showAvatar = !!username;

  return (
    <NodeFrame
      itemId={itemId}
      title={displayName || 'Agent'}
      subtitle={
        username ? (
          <span className="font-medium text-violet-500">@{username}</span>
        ) : undefined
      }
      actions={collapsed ? [] : actions}
      // Maximize → true fullscreen takeover (handled inside NodeFrame
      // via `setFullscreenItem`). We pass an empty onMaximize so the
      // green dot renders; NodeFrame does the rest.
      onMaximize={collapsed ? undefined : () => {}}
      // Double-clicking the header focuses the camera on this widget
      // — the gentler "zoom to fit" gesture the canvas used to fire
      // on any node double-click.
      onFocus={collapsed ? undefined : fitWidgetToScreen}
      // Minimize → dock the widget. Mac convention: minimize sends the
      // window to the dock; here we wire the same gesture to the
      // workspace dock bar at the bottom of the canvas.
      onMinimize={collapsed ? undefined : () => patchItemState(itemId, { placement: docked ? 'canvas' : 'docked' })}
      // Close stays available in both states so a collapsed puck is
      // never a dead end — but only as the always-on close affordance
      // in the chrome (hidden by the puck overlay). Pass `false` while
      // collapsed to suppress the chrome that's no longer reachable.
      showCloseDot={!collapsed}
      bodyPad={false}
      minWidth={300}
      minHeight={380}
      maxWidth={900}
      maxHeight={1200}
      collapsed={collapsed}
      cornerBadge={
        showAvatar ? (
          <Avatar
            seed={username}
            type={isHuman ? 'human' : 'agent'}
            src={avatarUrl}
            // Large, pronounced participant badge per the sketch — the
            // visual anchor of the widget. Sized to overlap the
            // header band and the body top edge when expanded, and
            // sit centred near the top of the puck when collapsed.
            className="h-14 w-14 ring-2 ring-background shadow-[0_4px_16px_rgba(0,0,0,0.18)]"
          />
        ) : null
      }
      onCornerBadgeClick={showAvatar ? toggleCollapsed : undefined}
      cornerBadgeAriaLabel={
        // Screen-reader only, which is exactly why it was missed: it was
        // hardcoded English AND said "widget", the internal term a person must
        // never be shown. Being hardcoded, `pnpm i18n:check` could not see it,
        // so the "zero /widget/i in en.json values" rule passed vacuously
        // (found by a browser pass, 2026-09-07).
        collapsed
          ? t('workspace.nodes.expand', {
              name: displayName || (isHuman ? t('workspace.nodes.person') : t('workspace.nodes.agent')),
            })
          : t('workspace.nodes.collapse')
      }
    >
      {/* On-device compute runner (hidden) — keeps a transport:'local' agent
          ONLINE by serving its completions from this owner's browser GPU. */}
      {localRunnerSpec && localRunnerSpec.entry && agentRefId && (
        <div aria-hidden style={{ position: 'absolute', left: -9999, top: 0, width: 1, height: 1, opacity: 0, overflow: 'hidden', pointerEvents: 'none' }}>
          <WidgetBridge
            src={localRunnerSpec.entry}
            workspaceId={workspace.id}
            itemId={itemId}
            title={t('workspace.node.onDeviceRunner')}
            widgetSpec={localRunnerSpec}
            external={localRunnerSpec.kind === 'iframe-external'}
            connectedAgentId={agentRefId}
          />
        </div>
      )}
      {view === 'welcome' && uiWidget && !chatMode && agentRefId && (
        <WidgetBridge
          src={uiWidget.spec.entry!}
          workspaceId={workspace.id}
          itemId={itemId}
          title={`${displayName || 'agent'} UI`}
          widgetSpec={uiWidget.spec}
          external={uiWidget.external}
          connectedAgentId={agentRefId}
          // The agent node's CURRENT chat — rides the connected-agent grant ref
          // so the UI widget (e.g. voice) reads it as agent.currentChatId and
          // binds its session to that thread. Switching chats remounts the
          // widget (chatId is folded into the iframe key in WidgetBridge).
          connectedChatId={chatId ?? undefined}
        />
      )}
      {view === 'welcome' && (!uiWidget || chatMode) && (
        <AgentChatPane
          username={username}
          isHuman={isHuman}
          chatId={chatId}
          prefillText={(item?.state.cache?.prefillText as string | undefined) ?? undefined}
          onChatCreated={(newId) => patchItemState(itemId, { chatId: newId })}
          onOpenContent={(contentId) => {
            // Dedup-first: if a canvas item already references this
            // content, focus + center it instead of spawning a
            // duplicate. Mirrors the spawn-event subscriber's
            // findFirstByRef path (see ~L235) — every spawn route
            // through this AgentNode MUST go through the same dedup,
            // otherwise the chip-click path (this handler) keeps
            // creating new widgets for the same refId on every click.
            //
            // Fire-and-forget the actual spawn — DocumentChip treats
            // any truthy return as "host handled it" and suppresses
            // its default navigate-away behavior.
            const existing = findFirstByRef(contentId);
            if (existing) {
              bringToFront(existing.id);
              const cx = existing.state.x + existing.state.width / 2;
              const cy = existing.state.y + existing.state.height / 2;
              try {
                flow.setCenter(cx, cy, { duration: 280, zoom: flow.getZoom() });
              } catch {
                // RF not yet measured — silent fallback.
              }
              return true;
            }
            void spawnFromAgent({
              parentItemId: itemId,
              type: 'content',
              refId: contentId,
            });
            return true;
          }}
          onOpenProfile={onOpenProfile}
          onReplayDelegation={onReplayDelegation}
          // "Turn on app building" on the guard card in the chat strip
          // flips THIS tile into its settings view (the same pane the
          // toolbar's Settings entry opens), where the Tools card lives.
          onOpenAgentSettings={() => {
            patchItemState(itemId, { view: 'settings' });
            return true;
          }}
          hostingAgentItemId={itemId}
          rootHostingAgentItemId={rootHostingAgentItemId}
          workspaceId={workspace.id}
          workspaceTabId={tabId ?? undefined}
        />
      )}
      {view === 'chats' && (
        <AgentChatsListPane
          username={username}
          activeChatId={chatId ?? undefined}
          // The currently-bound chat acts as the "parent" when surfacing
          // A2A sub-chats spawned from this widget — see the "From this
          // chat" section below.
          parentChatId={chatId ?? undefined}
          onPick={openChat}
          // Starting a "new" chat eager-creates a fresh thread and binds it as
          // the active chat, so the chat view AND the voice view both switch to
          // it (rather than voice staying on the old DM).
          onStartNew={startNewChat}
        />
      )}
      {view === 'settings' && !isHuman && <AgentSettingsPane username={username} />}
    </NodeFrame>
  );
}

/**
 * Shared section header for every state panel inside the agent widget.
 * The widget chrome already shows the agent name + @username; this row
 * names the *current state* (Welcome / Chats / Chat / Content /
 * Settings) so users always know which view they're in.
 */
function PaneTitle({
  children,
  trailing,
}: {
  children: React.ReactNode;
  trailing?: React.ReactNode;
}) {
  return (
    <div className="shrink-0 flex items-center justify-between gap-2 px-3 pt-2 pb-1.5">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {children}
      </div>
      {trailing}
    </div>
  );
}

/**
 * Per-participant fields we need to render an avatar + label in each
 * row. `accountType` discriminates the avatar fallback (human vs.
 * agent) and the optional username/displayName drive the seed-based
 * placeholder when `avatarUrl` is missing.
 */
type ChatSummaryParticipant = {
  id?: string;
  username?: string;
  displayName?: string;
  avatarUrl?: string;
  accountType?: 'human' | 'agent';
};

type ChatSummary = {
  id: string;
  /** Widened to include delegate variants — fetched with
   *  `?includeSubchats=1` so A2A sub-chats with this agent (e.g. one
   *  spawned by `@my-robutler` delegating to `@flux_schnell`) are
   *  surfaced here instead of being silently dropped by the inbox
   *  filter. */
  type?:
    | 'dm'
    | 'group'
    | 'delegate'
    | 'delegate-ephemeral'
    | 'delegate-manual';
  parentChatId?: string;
  name?: string | null;
  lastMessage?: string | null;
  lastMessageAt?: string | null;
  unreadCount?: number;
  participants?: ChatSummaryParticipant[];
};

type SubChatSummary = {
  id: string;
  type: 'delegate' | 'delegate-ephemeral' | 'delegate-manual';
  name: string | null;
  lastMessage: string | null;
  lastMessageAt: string | null;
  delegatedAgent: {
    id: string;
    username: string | null;
    displayName: string | null;
    avatarUrl: string | null;
  } | null;
};

const SUBCHAT_TYPES = new Set<NonNullable<ChatSummary['type']>>([
  'delegate',
  'delegate-ephemeral',
  'delegate-manual',
]);

/**
 * Tight 24×24 stacked-avatar group for chat rows. Single-participant
 * chats show one avatar centered; 2+ participants render two overlapping
 * 16px avatars (top-left + bottom-right) with a `+N` corner badge for
 * additional members. Mirrors the old `/chats` sidebar's group-chat
 * avatar but tuned for the tighter row density inside agent widgets.
 *
 * `fallbackSeed` is used when the participant list is empty (rare —
 * happens for chats where the only participant *is* the viewer, e.g.
 * a freshly-created draft).
 */
function RowAvatars({
  participants,
  fallbackSeed,
}: {
  participants: ChatSummaryParticipant[];
  fallbackSeed: string;
}) {
  if (participants.length === 0) {
    return (
      <Avatar
        seed={fallbackSeed}
        type="human"
        className="h-6 w-6 shrink-0"
      />
    );
  }
  if (participants.length === 1) {
    const p = participants[0];
    return (
      <Avatar
        seed={p.username ?? p.id ?? fallbackSeed}
        type={p.accountType ?? 'human'}
        src={p.avatarUrl ?? null}
        className="h-6 w-6 shrink-0"
      />
    );
  }
  const visible = participants.slice(0, 2);
  const extra = participants.length - visible.length;
  return (
    <div className="relative h-6 w-6 shrink-0">
      {visible.map((p, idx) => (
        <Avatar
          key={p.id ?? `${p.username}-${idx}`}
          seed={p.username ?? p.id ?? fallbackSeed}
          type={p.accountType ?? 'human'}
          src={p.avatarUrl ?? null}
          className={
            'absolute h-4 w-4 border-2 border-background ' +
            (idx === 0 ? 'top-0 left-0' : 'bottom-0 right-0')
          }
        />
      ))}
      {extra > 0 && (
        <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-muted border border-background flex items-center justify-center">
          <span className="text-[7px] font-semibold leading-none text-muted-foreground">
            +{extra}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Renders the user's chats that include this agent as a participant,
 * plus — when `parentChatId` is set — the A2A delegate sub-chats spawned
 * from the widget's currently-bound chat under a separate "From this
 * chat" section.
 *
 * We pull from `/api/messages` (the same endpoint the right edge panel
 * uses) and filter client-side by participant username — the API does
 * server-side filtering by agent UUID, which we don't have handy here.
 * Sub-chats come from `/api/me/chats/children?parentChatId=…`, which
 * returns delegate-typed rows that `getUserChats` deliberately filters
 * out of the inbox.
 */
function AgentChatsListPane({
  username,
  activeChatId,
  parentChatId,
  onPick,
  onStartNew,
}: {
  username: string;
  activeChatId?: string;
  parentChatId?: string;
  onPick: (chatId: string) => void;
  onStartNew: () => void;
}) {
  const t = useT();
  const { user: currentUser } = useAuth();
  const [chats, setChats] = useState<ChatSummary[] | null>(null);
  const [subChats, setSubChats] = useState<SubChatSummary[]>([]);
  useEffect(() => {
    if (!username) return;
    let alive = true;
    // `?includeSubchats=1` opts into delegate sub-chats so A2A
    // conversations where this agent participates as a callee (or as
    // an owner) show up in the widget's CHATS list. Without the flag
    // `/api/messages` would strip them via `getUserChats`'s NOT IN
    // filter — that was the original bug ("no subchats in the widget").
    fetch('/api/messages?includeSubchats=1')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!alive || !data) return;
        const raw: ChatSummary[] = data.chats ?? [];
        const mine = raw.filter((c) =>
          c.participants?.some(
            (p) => p.username?.toLowerCase() === username.toLowerCase(),
          ),
        );
        mine.sort((a, b) => {
          const ta = a.lastMessageAt ? Date.parse(a.lastMessageAt) : 0;
          const tb = b.lastMessageAt ? Date.parse(b.lastMessageAt) : 0;
          return tb - ta;
        });
        setChats(mine);
      })
      .catch(() => setChats([]));
    return () => {
      alive = false;
    };
  }, [username]);

  useEffect(() => {
    if (!parentChatId) {
      setSubChats([]);
      return;
    }
    let alive = true;
    fetch(`/api/me/chats/children?parentChatId=${encodeURIComponent(parentChatId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!alive || !data) return;
        const raw: SubChatSummary[] = data.children ?? [];
        setSubChats(raw);
      })
      .catch(() => {
        // Non-fatal: parent rows still render, just no From this chat.
        if (alive) setSubChats([]);
      });
    return () => {
      alive = false;
    };
  }, [parentChatId]);

  if (!username) {
    return (
      <div className="h-full flex items-center justify-center text-xs text-muted-foreground">
        {t('workspace.node.missingAgentRef')}
      </div>
    );
  }

  /**
   * Avatars shown in each chat row: every SEMANTIC party of the chat
   * EXCEPT this widget's host agent. The widget header already names
   * the host, so repeating its avatar in every row is noise.
   *
   * "Semantic party" differs from "row in chat_participants" only for
   * A2A sub-chats: those rows pull the parent chat's human owner in
   * as a participant so live `message.created` broadcasts can fan out
   * to their /ws connection (see `insertParticipants` in
   * `lib/messaging/delegate-subchats.ts`), but the human is an
   * observer, not a party to the agent-to-agent conversation. For
   * `@my-robutler -> @flux_schnell` viewed in flux's widget the user
   * expects to see `@my-robutler`, not their own "R" avatar — so we
   * drop non-agent rows for A2A.
   *
   * For DMs / groups the viewer IS a party (server already stripped
   * them from `participants`, so `chatParticipants` reconstitutes
   * them via useAuth).
   */
  const avatarParticipants = (chat: ChatSummary): ChatSummaryParticipant[] => {
    const lowerHost = username.toLowerCase();
    const isA2A = chat.type ? SUBCHAT_TYPES.has(chat.type) : false;

    if (isA2A) {
      return (chat.participants ?? []).filter(
        (p) =>
          p.accountType === 'agent' &&
          p.username?.toLowerCase() !== lowerHost,
      );
    }

    return chatParticipants(chat.participants, currentUser).filter(
      (p) => p.username?.toLowerCase() !== lowerHost,
    );
  };

  /**
   * Default title for a chat row: comma-joined `@username` mentions of
   * everyone in the conversation EXCEPT the widget's host agent. Same
   * projection as `avatarParticipants` so the avatar stack and the
   * title line always describe the same set of parties — no more
   * "FLUX.1 [schnell], my-robutler" when viewing flux's widget.
   *
   * @-mentions are tinted (link-tone) but not interactive: the row
   * itself is the click target (opens the chat), and nesting another
   * interactive element inside that button would break HTML
   * semantics. The "@" prefix + accent color is the established
   * mention affordance (mirrors the markdown @nli renderer).
   */
  const renderTitle = (chat: ChatSummary): React.ReactNode => {
    const parts = avatarParticipants(chat);
    if (parts.length === 0) {
      // Fall back to an explicit custom name (rare for widgets — group
      // chats with a renamed title) or to the host's own handle so the
      // row is never blank.
      return chat.name ?? `@${username}`;
    }
    return (
      <>
        {parts.map((p, idx) => {
          const handle = p.username ?? p.displayName ?? 'unknown';
          return (
            <span key={p.id ?? `${handle}-${idx}`}>
              <span className="text-primary/90">@{handle}</span>
              {idx < parts.length - 1 && (
                <span className="text-muted-foreground">, </span>
              )}
            </span>
          );
        })}
      </>
    );
  };

  const renderRow = (
    id: string,
    title: React.ReactNode,
    last: string,
    when: string,
    isActive: boolean,
    avatars: ChatSummaryParticipant[],
    badge?: { count: number } | { label: string; title?: string },
  ) => (
    <button
      type="button"
      onClick={() => onPick(id)}
      className={
        'w-full text-left px-2 py-1.5 rounded-md transition-colors flex items-center gap-2 ' +
        (isActive ? 'bg-foreground/10' : 'hover:bg-foreground/5')
      }
    >
      <RowAvatars participants={avatars} fallbackSeed={id} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-1.5">
          <div className="truncate text-xs font-medium text-foreground min-w-0 flex-1">{title}</div>
          {when && (
            <div className="ml-auto shrink-0 text-[10px] text-muted-foreground">{when}</div>
          )}
        </div>
        <div className="truncate text-[11px] text-muted-foreground">{last}</div>
      </div>
      {badge && 'count' in badge && badge.count > 0 && (
        <span className="shrink-0 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold leading-none text-white">
          {badge.count > 99 ? '99+' : badge.count}
        </span>
      )}
      {badge && 'label' in badge && (
        <span
          title={badge.title}
          className="shrink-0 inline-flex items-center rounded-full bg-foreground/10 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-muted-foreground"
        >
          {badge.label}
        </span>
      )}
    </button>
  );

  // De-dupe: any chat already in the From this chat sub-chats panel
  // must not also appear in the inbox list above. The /api/messages
  // endpoint (with includeSubchats=1) now returns delegate rows that
  // have this agent as a participant, so a sub-chat spawned from the
  // host widget's bound parent would otherwise render twice — once in
  // the main list, once under From this chat.
  const subChatIds = new Set(subChats.map((s) => s.id));
  const inboxChats = (chats ?? []).filter((c) => !subChatIds.has(c.id));

  const hasInbox = inboxChats.length > 0;
  const hasSub = subChats.length > 0;
  const isLoading = chats === null;

  return (
    <div className="h-full flex flex-col">
      <PaneTitle
        trailing={
          <button
            type="button"
            onClick={onStartNew}
            title={t('workspace.node.startNewChat')}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-foreground/80 hover:text-foreground rounded-full px-2 py-0.5 hover:bg-foreground/5 transition-colors"
          >
            <PenSquare className="h-3 w-3" />
            New
          </button>
        }
      >
        Chats
      </PaneTitle>
      <div className="nowheel ws-selectable flex-1 min-h-0 overflow-y-auto px-2 pb-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size="sm" tone="muted" />
          </div>
        ) : !hasInbox && !hasSub ? (
          <div className="px-3 py-6 text-center text-xs text-muted-foreground space-y-2">
            <div>No chats yet with @{username}.</div>
            <button
              type="button"
              onClick={onStartNew}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-foreground/10 hover:bg-foreground/15 text-foreground transition-colors"
            >
              <PenSquare className="h-3.5 w-3.5" />
              {t('workspace.node.startNewChat')}
            </button>
          </div>
        ) : (
          <>
            {hasInbox && (
              <ul className="space-y-0.5">
                {inboxChats.map((chat) => {
                  const isActive = chat.id === activeChatId;
                  const last = chat.lastMessage?.trim() || 'No messages yet';
                  const when = chat.lastMessageAt ? formatRelativeTime(chat.lastMessageAt) : '';
                  const isA2A = chat.type ? SUBCHAT_TYPES.has(chat.type) : false;
                  const avatars = avatarParticipants(chat);
                  const title = renderTitle(chat);
                  // Agent-to-agent chats get a chip badge to tell them apart
                  // from regular DMs and groups; otherwise the unread counter
                  // badge takes that slot (preserves the existing notification
                  // affordance). The label is translated and deliberately not
                  // the literal "A2A": that is the internal term, and a person
                  // must never be shown it. `recent-tabs.tsx` renders the same
                  // badge from the same TWO keys, label and tooltip, so the two
                  // surfaces cannot drift. An earlier revision of this comment
                  // claimed that while passing only the label, which left the
                  // agent-node badge with no tooltip at all.
                  const badge: { count: number } | { label: string; title?: string } = isA2A
                    ? {
                        label: t('workspace.panels.agentSubChatBadge'),
                        title: t('workspace.panels.agentSubChat'),
                      }
                    : { count: chat.unreadCount ?? 0 };
                  return (
                    <li key={chat.id}>
                      {renderRow(chat.id, title, last, when, isActive, avatars, badge)}
                    </li>
                  );
                })}
              </ul>
            )}
            {hasSub && (
              <>
                <div className="px-2 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('workspace.node.fromThisChat')}
                </div>
                <ul className="space-y-0.5">
                  {subChats.map((sub) => {
                    const isActive = sub.id === activeChatId;
                    const last = sub.lastMessage?.trim() || 'No messages yet';
                    const when = sub.lastMessageAt ? formatRelativeTime(sub.lastMessageAt) : '';
                    // Mirror DelegateSubChatPreview's labeling: name rows
                    // by the DELEGATED agent (the recipient), not by the
                    // host widget agent. That's what makes the section
                    // useful — it tells the user which agent the host
                    // delegated to.
                    const delegatee =
                      sub.delegatedAgent?.username ||
                      sub.delegatedAgent?.displayName ||
                      'agent';
                    // Tint to match inbox-row mentions so the eye
                    // groups From this chat rows visually with the
                    // A2A inbox rows above.
                    const title = (
                      <span className="text-primary/90">@{delegatee}</span>
                    );
                    // Same rule as the inbox rows: show every
                    // participant EXCEPT this widget's host agent. For
                    // From this chat sub-chats the only non-host
                    // participant carried by /api/me/chats/children is
                    // the delegatee — exactly what we want.
                    const avatars: ChatSummaryParticipant[] = sub.delegatedAgent
                      ? [{
                          id: sub.delegatedAgent.id,
                          username: sub.delegatedAgent.username ?? undefined,
                          displayName: sub.delegatedAgent.displayName ?? undefined,
                          avatarUrl: sub.delegatedAgent.avatarUrl ?? undefined,
                          accountType: 'agent' as const,
                        }]
                      : [];
                    return (
                      <li key={sub.id}>
                        {renderRow(sub.id, title, last, when, isActive, avatars, {
                          label: t('workspace.panels.agentSubChatBadge'),
                          title: t('workspace.panels.agentSubChat'),
                        })}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Memoized export. Node only re-renders when its id, selection state,
 * or drag state change — the per-item store subscription already
 * handles fine-grained data updates through `useWorkspaceItem(id)`.
 */
export const AgentNode = memo(AgentNodeImpl, (a, b) => {
  return (
    a.id === b.id &&
    a.selected === b.selected &&
    a.dragging === b.dragging
  );
});

/**
 * Welcome / chat pane. Renders the embedded `ChatView` directly so the
 * agent's OWN welcome (the draft-mode greeting + suggested prompts the
 * agent defines) is what the user sees on first land — same experience
 * as the `/[username]` page. When `chatId` is set we render the live
 * chat instead. No `PaneTitle` here: the widget chrome already shows
 * the agent name + @username; a second "Welcome" / Chat label would
 * just be noise on top of the agent's own greeting.
 */
function AgentChatPane({
  username,
  isHuman,
  chatId,
  prefillText,
  onChatCreated,
  onOpenContent,
  onOpenProfile,
  onReplayDelegation,
  onOpenAgentSettings,
  hostingAgentItemId,
  rootHostingAgentItemId,
  workspaceId,
  workspaceTabId,
}: AgentChatPaneProps) {
  const t = useT();
  const { user, loading: authLoading } = useAuth();
  if (!username) {
    return (
      <div className="h-full flex items-center justify-center text-xs text-muted-foreground">
        {t('workspace.node.missingAgentRef')}
      </div>
    );
  }
  // Anon visitors: render a lightweight, working welcome screen with a
  // real input. On send we open the signup dialog and stash the typed
  // text via the signup gate so the post-OAuth clone-from flow starts
  // a real chat with that prompt as the first message in the cloned
  // workspace. We never POST anything from the anon textarea itself.
  if (!authLoading && !user) {
    return (
      <div className="nowheel ws-selectable h-full w-full overflow-hidden">
        <AnonAgentChatPreview
          username={username}
          prefillText={prefillText ?? ''}
        />
      </div>
    );
  }
  return (
    <div className="nowheel ws-selectable h-full w-full overflow-hidden">
      <ChatView
        key={chatId ?? `draft:${username}`}
        chatId={chatId ?? undefined}
        draft={
          chatId
            ? undefined
            : {
                participantUsername: username,
                prefillText: prefillText ?? '',
                // Skip the profile fetch the greeting would otherwise
                // wait on: the spawning surface already resolved this
                // participant's kind into `cache.accountType`.
                participantIsAgent: !isHuman,
              }
        }
        onChatCreated={onChatCreated}
        embedded
        hideHeader
        onOpenContent={onOpenContent}
        onOpenProfile={onOpenProfile}
        onReplayDelegation={onReplayDelegation}
        onOpenAgentSettings={onOpenAgentSettings}
        hostingAgentItemId={hostingAgentItemId}
        rootHostingAgentItemId={rootHostingAgentItemId}
        workspaceId={workspaceId}
        workspaceTabId={workspaceTabId}
      />
    </div>
  );
}

type AgentChatPaneProps = {
  username: string;
  /**
   * Whether this participant is a person rather than an agent, read off
   * the item's `cache.accountType` by the parent. Forwarded to
   * `ChatView`'s draft so the greeting paints in the right register
   * without waiting on a profile fetch.
   */
  isHuman?: boolean;
  chatId?: string | null;
  /**
   * Optional draft text to seed the composer with the first time the
   * agent widget is opened. Consumed by `ChatView`'s prefill effect,
   * which also focuses the textarea and parks the caret at the end of
   * the string. Only meaningful in draft mode (no `chatId`); once the
   * user sends a message the chat is created and the prefill never
   * re-applies. Used by the "Create agent / Create widget" funnel out
   * of search-everything to land users directly in `@robutler.factory`
   * with the seed prompt ready to refine.
   */
  prefillText?: string;
  onChatCreated: (chatId: string) => void;
  onOpenContent?: ChatHostHandlers['onOpenContent'];
  onOpenProfile?: ChatHostHandlers['onOpenProfile'];
  onReplayDelegation?: ChatHostHandlers['onReplayDelegation'];
  onOpenAgentSettings?: ChatHostHandlers['onOpenAgentSettings'];
  hostingAgentItemId?: ChatHostHandlers['hostingAgentItemId'];
  rootHostingAgentItemId?: ChatHostHandlers['rootHostingAgentItemId'];
  /**
   * Workspace context — forwarded to `ChatView` so it can stamp
   * `x-workspace-id` + `x-workspace-tab-id` on every chat send. The
   * server's `createPortalContext` reads these and mounts the
   * `workspace.widgets.*` agent tools, then routes their dispatches
   * back to THIS tab's `WidgetCommandBus` via `workspace.bus`.
   */
  workspaceId?: string;
  workspaceTabId?: string;
};

function AgentSettingsPane({ username }: { username: string }) {
  const t = useT();
  if (!username) {
    return (
      <div className="h-full flex items-center justify-center text-xs text-muted-foreground">
        {t('workspace.node.missingAgentRef')}
      </div>
    );
  }
  // AgentSettingsPanel was designed for the full /[username] settings
  // page and ships its own sticky save bar — but its `pb-[68px]`
  // mobile-nav offset and `-mx-4 px-4` assume the page's own px-4
  // padding. Inside the widget we want:
  //   - vertical scroll owned by the panel (not the page)
  //   - the sticky save bar pinned flush to the BOTTOM of the widget
  //     body, with no mobile-nav offset
  //   - horizontal clipping
  // Layout: the shell is a relatively-positioned flex column.
  // Inside it, the scrollable area takes flex-1 and the
  // .sticky.bottom-0 save bar inside AgentSettingsPanel is upgraded
  // to position: absolute (see globals.css) so it pins to the bottom
  // of the shell regardless of scroll position.
  return (
    <div className="ws-agent-settings-shell h-full flex flex-col">
      <PaneTitle>Settings</PaneTitle>
      <div className="nowheel ws-selectable ws-agent-settings flex-1 min-h-0 w-full overflow-y-auto overflow-x-hidden">
        <div className="px-3 pt-2">
          <AgentSettingsPanel agentIdOrName={username} />
        </div>
      </div>
    </div>
  );
}

